import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * פלנוגרמה: איפה כל מוצר עומד על המדף (מיגרציה 063).
 *
 * שלוש רמות — יחידה, מדף, מוצר — כי זה מה שמצייר את התמונה.
 */
export type PlanogramSlot = {
  id: string;
  product_id: string;
  product: string;
  image_path: string | null;
  facings: number;
  note: string | null;
  sort_order: number;
};

export type PlanogramShelf = {
  id: string;
  label: string | null;
  sort_order: number;
  slots: PlanogramSlot[];
};

export type Planogram = {
  id: string;
  name: string;
  note: string | null;
  /** הסיווג (מיגרציה 080). null = עוד לא סווגה. */
  area_id: string | null;
  area: string | null;
  department_id: string | null;
  department: string | null;
  shelves: PlanogramShelf[];
};

/** אותה יחידה, בלוח שמציג כמה סניפים. */
export type BranchPlanogram = Planogram & {
  branch_id: string;
  branch: string;
  branch_color: string | null;
};

const KEY = ['planograms'] as const;

export function usePlanograms(branchId: string | null) {
  return useQuery({
    queryKey: [...KEY, branchId ?? 'none'] as const,
    enabled: branchId !== null && branchId !== '',
    queryFn: async (): Promise<Planogram[]> => {
      const { data, error } = await supabase.rpc('planogram_board', {
        p_branch: branchId ?? '',
      });
      if (error) throw error;
      return (data as unknown as Planogram[]) ?? [];
    },
  });
}

/**
 * הלוח של כמה סניפים יחד (מיגרציה 081).
 *
 * רשימה ריקה = כל הסניפים שמותר לראות, וזה המצב שבו נפתחת
 * ההשוואה. המפתח כולל את הרשימה כדי שמעבר בין בחירות לא יציג
 * תשובה של בחירה אחרת.
 */
export function usePlanogramBoards(branchIds: string[]) {
  const key = [...branchIds].sort().join(',');

  return useQuery({
    queryKey: [...KEY, 'multi', key] as const,
    queryFn: async (): Promise<BranchPlanogram[]> => {
      const { data, error } = await supabase.rpc('planogram_boards', {
        p_branches: branchIds,
      });
      if (error) throw error;
      return (data as unknown as BranchPlanogram[]) ?? [];
    },
  });
}

export function usePlanogramMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });

  /**
   * יצירת יחידה בסניף אחד או בכמה (מיגרציה 081).
   *
   * מחזירה כמה נוצרו, כי סניף שכבר יש בו יחידה באותו שם מדולג.
   * המדפים נוצרים בשרת באותה פעולה: יחידה בלי מדפים אינה ניתנת
   * לעריכה, ושתי קריאות נפרדות יכלו להשאיר אותה כזו.
   */
  const createUnit = useMutation({
    mutationFn: async (input: {
      branchIds: string[];
      name: string;
      area_id: string | null;
      department_id: string | null;
    }) => {
      const { data, error } = await supabase.rpc('create_planogram_units', {
        p_branches: input.branchIds,
        p_name: input.name,
        p_area: input.area_id ?? undefined,
        p_department: input.department_id ?? undefined,
      });
      if (error) throw error;
      return (data as unknown as number) ?? 0;
    },
    onSuccess: refresh,
  });

  /** שיוך היחידה לאזור ולמחלקה. */
  const setZones = useMutation({
    mutationFn: async (input: {
      id: string;
      area_id: string | null;
      department_id: string | null;
    }) => {
      const { error } = await supabase
        .from('planograms')
        .update({
          area_id: input.area_id,
          department_id: input.department_id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const renameUnit = useMutation({
    mutationFn: async (input: { id: string; name: string; note: string | null }) => {
      const { error } = await supabase
        .from('planograms')
        .update({ name: input.name, note: input.note, updated_at: new Date().toISOString() })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const removeUnit = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('planograms').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const addShelf = useMutation({
    mutationFn: async (input: { planogram_id: string; sort_order: number }) => {
      const { error } = await supabase.from('planogram_shelves').insert(input);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const removeShelf = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('planogram_shelves').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const addSlot = useMutation({
    mutationFn: async (input: { shelf_id: string; product_id: string; sort_order: number }) => {
      const { error } = await supabase.from('planogram_slots').insert(input);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const setFacings = useMutation({
    mutationFn: async (input: { id: string; facings: number }) => {
      const { error } = await supabase
        .from('planogram_slots')
        .update({ facings: input.facings })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const removeSlot = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('planogram_slots').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /**
   * שכפול היחידה לסניפים נוספים (מיגרציה 064). מחזירה כמה נוצרו,
   * כי סניף שכבר יש בו יחידה באותו שם מדולג.
   */
  const copyUnit = useMutation({
    mutationFn: async (input: { id: string; branchIds: string[] }) => {
      const { data, error } = await supabase.rpc('copy_planogram', {
        p_planogram: input.id,
        p_branches: input.branchIds,
      });
      if (error) throw error;
      return data ?? 0;
    },
    onSuccess: refresh,
  });

  /** הזזה בין מדפים ובתוך מדף, בפקודה אחת. */
  const moveSlot = useMutation({
    mutationFn: async (input: { slotId: string; shelfId: string; order: string[] }) => {
      const { error } = await supabase.rpc('move_planogram_slot', {
        p_slot: input.slotId,
        p_shelf: input.shelfId,
        p_order: input.order,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return {
    createUnit,
    setZones,
    renameUnit,
    removeUnit,
    addShelf,
    removeShelf,
    addSlot,
    setFacings,
    removeSlot,
    moveSlot,
    copyUnit,
  };
}
